import * as Sentry from '@sentry/react';
import type { Span } from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';
import { waitUntilQuiet } from './waitUntilQuiet';

const batches = new Map<
  string,
  { span: Span; open: number; failed: boolean }
>();

/**
 * Starts the span of a write and returns the function that settles it. The
 * span ends when every overlapping write of the same action, such as a bulk
 * publish, has settled and the queries they invalidated have refetched. A
 * failed write ends it with an error status.
 */
export function startWriteSpan({
  action,
  attributes,
  queryClient,
}: {
  action: string;
  attributes: { 'team.id': string; portal: 'pm' | 'owner' };
  queryClient: QueryClient;
}) {
  const batch = batches.get(action) ?? {
    span: Sentry.startInactiveSpan({
      name: `write.${action}`,
      op: 'ui.write',
      attributes: {
        ...attributes,
        'write.action': action,
        // The first path segment names the page, such as `expense`.
        'write.page': window.location.pathname.split('/')[1],
      },
    }),
    open: 0,
    failed: false,
  };
  batch.open += 1;
  batches.set(action, batch);

  let settled = false;

  return (failed: boolean) => {
    if (settled) return;
    settled = true;
    batch.open -= 1;
    batch.failed ||= failed;
    if (batch.open > 0) return;

    batches.delete(action);
    if (batch.failed) {
      batch.span.setStatus({ code: 2, message: 'internal_error' });
      batch.span.end();
      return;
    }

    waitUntilQuiet({
      // Polling queries refetch on their own and would keep the write open.
      isBusy: () =>
        queryClient.isFetching({
          predicate: (query) =>
            !query.observers.some(
              (observer) => observer.options.refetchInterval
            ),
        }) > 0,
      quietMs: 150,
      subscribe: (listener) => queryClient.getQueryCache().subscribe(listener),
      timeoutMs: 15_000,
    }).then((result) => {
      if (result !== 'quiet') {
        batch.span.setStatus({ code: 2, message: 'cancelled' });
      }
      batch.span.end();
    });
  };
}
